import React from 'react';
import { Users, Award, TrendingUp, CheckCircle } from 'lucide-react';
import { ProcessedMember } from '../types';
import { formatNumber } from '../utils/helpers';

interface SummaryCardsProps {
  members: ProcessedMember[];
  totalMembers: number;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ members, totalMembers }) => {
  const qualifiedCount = members.length;
  const topThree = members.slice(0, 3);
  const avgScore = qualifiedCount > 0 ? members.reduce((sum, m) => sum + m.finalScore, 0) / qualifiedCount : 0;

  const cards = [
    {
      title: 'Total Members',
      value: formatNumber(totalMembers),
      icon: Users,
      color: 'primary',
      description: 'Members processed'
    },
    {
      title: 'Qualified Members',
      value: formatNumber(qualifiedCount),
      icon: CheckCircle,
      color: 'success',
      description: 'Meet contribution criteria'
    },
    {
      title: 'Average Score',
      value: avgScore.toFixed(1),
      icon: TrendingUp,
      color: 'accent',
      description: 'Overall performance'
    },
    {
      title: 'Top Hot Seat',
      value: topThree[0]?.memberName || 'N/A',
      icon: Award,
      color: 'secondary',
      description: 'Highest scoring member'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => {
        const Icon = card.icon;
        const colorClasses = {
          primary: 'bg-primary-100 text-primary-600',
          success: 'bg-success-100 text-success-600',
          accent: 'bg-accent-100 text-accent-600',
          secondary: 'bg-secondary-100 text-secondary-600'
        };

        return (
          <div key={index} className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 mb-1">
                  {card.title}
                </p>
                <p className="text-2xl font-bold text-secondary-900">
                  {card.value}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  {card.description}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[card.color as keyof typeof colorClasses]}`}>
                <Icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SummaryCards;
